/**
 * Style4/Style5 (Theater / Game-Show) style-specific section renderers.
 * Extracted from Classic5050Style4.jsx for use in GridEngine.
 * Dark theme with animated jackpot digits, countdown units, stats grid.
 */
import React, { useState, useEffect, useRef } from 'react'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import { formatCurrencyValue } from '../../utils/formatCurrency'

// Animated number display for jackpot
function AnimatedNumber({ value, duration = 1000 }) {
  const [displayValue, setDisplayValue] = useState(0)
  const previousValue = useRef(0)

  useEffect(() => {
    const startValue = previousValue.current
    const endValue = value
    const startTime = Date.now()

    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(startValue + (endValue - startValue) * eased)
      setDisplayValue(current)
      if (progress < 1) requestAnimationFrame(animate)
    }

    requestAnimationFrame(animate)
    previousValue.current = endValue
  }, [value, duration])

  return <>{displayValue.toLocaleString()}</>
}

// Jackpot display with slot-machine style digits
function JackpotDisplay({ amount, language }) {
  const digits = formatCurrencyValue(amount, { language }).split('')
  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-center">
      {digits.map((char, i) => (
        <span
          key={i}
          className={`inline-flex items-center justify-center ${
            char === '$' || char === ','
              ? 'text-3xl md:text-5xl font-black'
              : 'w-12 h-16 md:w-16 md:h-20 rounded-xl text-3xl md:text-5xl font-black shadow-lg'
          }`}
          style={
            char !== '$' && char !== ','
              ? {
                  backgroundColor: 'color-mix(in srgb, var(--charity-primary-contrast) 15%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 30%, transparent)',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  color: 'var(--charity-primary-contrast)',
                }
              : { color: 'var(--charity-primary-contrast)' }
          }
        >
          {char}
        </span>
      ))}
    </div>
  )
}

// Countdown unit block
function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center shadow-lg"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--charity-primary-contrast) 10%, transparent)',
          borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 30%, transparent)',
          borderWidth: '2px',
          borderStyle: 'solid',
        }}
      >
        <span className="text-3xl md:text-4xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs font-bold uppercase tracking-wider mt-2 opacity-70" style={{ color: 'var(--charity-primary-contrast)' }}>
        {label}
      </span>
    </div>
  )
}

// Hero: Dark theater/game-show with animated jackpot, countdown, stats, CTA
export function TheaterHeroSection({
  mainDraw, t, editMode, handleTranslationUpdate,
  purchaseNavigate, timeLeft, language,
}) {
  const c = t
  if (!mainDraw) return null

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const ticketsSold = mainDraw?.total_tickets_sold
  const totalPlayers = mainDraw?.total_players ?? ticketsSold ?? 0

  return (
    <div
      className="pt-8 pb-12 px-4 relative"
      style={{
        background: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
      }}
    >
      {/* Decorative glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }} aria-hidden="true" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }} aria-hidden="true" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--charity-accent) 20%, transparent)',
              borderColor: 'color-mix(in srgb, var(--charity-accent) 50%, transparent)',
              borderWidth: '1px', borderStyle: 'solid',
            }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--charity-accent)' }} />
            <span className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--charity-accent)' }}>
              <InlineTranslationEdit translationKey="classic5050.liveNow" value={c?.('classic5050.liveNow', 'Live Now')} fallback="Live Now" onUpdate={handleTranslationUpdate} editMode={editMode} />
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-4" style={{ color: 'var(--charity-primary-contrast)' }}>
            <InlineTranslationEdit translationKey="classic5050.heroTitle" value={c?.('classic5050.heroTitle', 'THE BIG JACKPOT')} fallback="THE BIG JACKPOT" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </h1>
          <p className="text-xl opacity-90" style={{ color: 'var(--charity-primary-contrast)' }}>
            <InlineTranslationEdit translationKey="classic5050.heroSubtitle" value={c?.('classic5050.heroSubtitle', 'Your chance to win BIG while supporting the community!')} fallback="Your chance to win BIG while supporting the community!" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </p>
          <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
        </div>

        <div className="bg-white/10 dark:bg-black/20 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-2xl"
          style={{
            borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 30%, transparent)',
            borderWidth: '1px', borderStyle: 'solid',
          }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Trophy className="w-8 h-8" style={{ color: 'var(--charity-primary-contrast)' }} />
              <span className="text-lg font-bold uppercase tracking-widest" style={{ color: 'var(--charity-primary-contrast)' }}>
                {c?.('gameShow.currentJackpot', 'Current Jackpot')}
              </span>
              <Trophy className="w-8 h-8" style={{ color: 'var(--charity-primary-contrast)' }} />
            </div>
            <div className="flex justify-center mb-6">
              <JackpotDisplay amount={jackpotAmount} language={language} />
            </div>
            <p className="opacity-80" style={{ color: 'var(--charity-primary-contrast)' }}>
              {c?.('gameShow.winnerTakes', 'Winner takes 50% of the pot!')}
            </p>
          </div>

          {/* Countdown */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <CountdownUnit value={timeLeft?.days || 0} label={c?.('gameShow.days', 'Days')} />
            <CountdownUnit value={timeLeft?.hours || 0} label={c?.('gameShow.hours', 'Hours')} />
            <CountdownUnit value={timeLeft?.minutes || 0} label={c?.('gameShow.minutes', 'Mins')} />
            <CountdownUnit value={timeLeft?.seconds || 0} label={c?.('gameShow.seconds', 'Secs')} />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-6 mb-10">
            {ticketsSold != null && (
              <div className="text-center p-4 rounded-xl"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--charity-primary-contrast) 10%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 20%, transparent)',
                  borderWidth: '1px', borderStyle: 'solid',
                }}>
                <Ticket className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--charity-primary-contrast)' }} />
                <p className="text-3xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}><AnimatedNumber value={ticketsSold} /></p>
                <p className="text-sm opacity-70" style={{ color: 'var(--charity-primary-contrast)' }}>{c?.('gameShow.ticketsSold', 'Tickets Sold')}</p>
              </div>
            )}
            <div className="text-center p-4 rounded-xl"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--charity-primary-contrast) 10%, transparent)',
                borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 20%, transparent)',
                borderWidth: '1px', borderStyle: 'solid',
              }}>
              <Users className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--charity-primary-contrast)' }} />
              <p className="text-3xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}><AnimatedNumber value={totalPlayers} /></p>
              <p className="text-sm opacity-70" style={{ color: 'var(--charity-primary-contrast)' }}>{c?.('gameShow.players', 'Players')}</p>
            </div>
            <div className="text-center p-4 rounded-xl"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--charity-primary-contrast) 10%, transparent)',
                borderColor: 'color-mix(in srgb, var(--charity-primary-contrast) 20%, transparent)',
                borderWidth: '1px', borderStyle: 'solid',
              }}>
              <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--charity-primary-contrast)' }} />
              <p className="text-3xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>50%</p>
              <p className="text-sm opacity-70" style={{ color: 'var(--charity-primary-contrast)' }}>{c?.('gameShow.toWinner', 'To Winner')}</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => purchaseNavigate?.('/purchase')}
              className="group relative inline-flex items-center gap-3 px-10 py-5 text-xl font-black rounded-2xl shadow-xl transform hover:scale-105 transition-all"
              style={{
                backgroundColor: 'var(--charity-primary-contrast)',
                color: 'var(--charity-primary)',
              }}
            >
              <Zap className="w-6 h-6" />
              <span>{c?.('gameShow.getTickets', 'GET YOUR TICKETS NOW')}</span>
              <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
